/**
 * VX-3 레인 C — /apt/[id] 상세의 순수 판정 (의존 0 · 클라이언트에서도 import 가능 · vitest 잠금 `vx3-detail.test.tsx`).
 *
 *   D4 시간대 전환(낮·노을·밤) — 토글 표시 조건 · 시각 → 컷 · 라디오 키보드 · 계측 상한
 *   D5 전체 화면 갤러리 — 장면 순서(마지막 «뒤» 관심고객 카드) · 넘기기 · 확대/이동 한계
 *   R17 신고 — 같은 컷 중복 방지 키
 *   R12 구조화 데이터 — Residence image 배열
 *   R5 슬라이더 폴백 — 2단 이미지 주소 검사
 *
 * ⛔ 여기서 이미지를 «고르지» 않는다. 우선순위(공식 > 생성 > 카드)는 L2 리졸버(lib/site-image) 한 곳이다 — 이 파일은 그 결과를 받아 모양만 정한다.
 */
import { CODE_FLAG_DEFAULTS } from '@/lib/config/flag-defaults';

/* ───────────────────────────── 컷 목록 ───────────────────────────── */

/** 상세 클라이언트(히어로 전환 · 라이트박스)가 받는 컷 1장. jpg = master JPEG 절대 URL(자체 도메인), webp = 표시용 상대경로. */
export interface DetailShot {
  shot: string;
  jpg: string;
  webp?: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
}

type MediaLike = { kind?: string | null; jpg?: string | null; webp?: string | null; alt?: string | null; caption?: string | null; width?: number | null; height?: number | null; shot?: string | null };

/** 리졸버가 준 갤러리(SiteMedia[] · S1→S4 순) → 상세 컷 목록. 생성 컷만 · 컷 키/주소 중복 제거 · 최대 4. */
export function detailShotsOf(media: readonly (MediaLike | null | undefined)[] | null | undefined): DetailShot[] {
  const out: DetailShot[] = [];
  const seen = new Set<string>();
  for (const m of media ?? []) {
    if (!m || m.kind !== 'generated') continue;
    const jpg = typeof m.jpg === 'string' ? m.jpg.trim() : '';
    const shot = typeof m.shot === 'string' ? m.shot.trim() : '';
    if (!jpg || !shot || seen.has(shot) || seen.has(jpg)) continue;
    seen.add(shot); seen.add(jpg);
    out.push({
      shot, jpg,
      ...(typeof m.webp === 'string' && m.webp.trim() ? { webp: m.webp.trim() } : {}),
      alt: (m.alt ?? '').trim(),
      caption: (m.caption ?? '').trim(),
      width: Number(m.width) > 0 ? Number(m.width) : 16,
      height: Number(m.height) > 0 ? Number(m.height) : 9,
    });
    if (out.length >= 4) break;
  }
  return out;
}

/* ───────────────────────────── D4 시간대 전환 ───────────────────────────── */

export const HERO_SHOT_DAY = 'S1_aerial_day';
export const HERO_SHOT_GOLDEN = 'S2_aerial_golden';
export const HERO_SHOT_NIGHT = 'S4_night';

/** 토글 순서·라벨. S3(눈높이)은 시간대가 아니다 — 토글에 서지 않는다(갤러리·라이트박스에는 선다). */
export const HERO_TIME_ORDER: readonly { shot: string; label: string }[] = [
  { shot: HERO_SHOT_DAY, label: '낮' },
  { shot: HERO_SHOT_GOLDEN, label: '노을' },
  { shot: HERO_SHOT_NIGHT, label: '밤' },
];

/** 토글이 서려면 그 현장의 컷이 3종 이상이어야 한다(1~2장이면 숨김 — 판정증분 D4). */
export const HERO_TIME_MIN_SHOTS = 3;

/**
 * 방문 시각으로 히어로 컷을 «스스로» 바꿀지. VX-3 레인 C 보수 판정으로 «꺼 둔다»(false).
 *   · SSR HTML 은 어느 쪽이든 S1 그대로다(전환은 수화 뒤 겹침 층으로만 한다 — <img src> 불변).
 *   · 꺼 둔 이유: 이 레인은 빌드·브라우저가 금지라 LCP 재측정 여부(같은 크기 그림이 뒤늦게 그려질 때)와
 *     촬영·채점의 시각 의존(18시 이후 촬영본이 달라진다)을 실측하지 못했다. 구현·잠금은 되어 있다 —
 *     통합에서 재고 켤 때 이 값만 true 로 바꾼다(대장 ledger_VX3-C.md).
 */
export const HERO_TIME_AUTO_SWITCH = CODE_FLAG_DEFAULTS.hero_time_auto_switch;

export interface HeroTimeOption extends DetailShot { label: string }

/**
 * 토글에 설 컷. 빈 배열 = 토글 숨김.
 *   · 컷이 3종 미만이면 숨김.
 *   · 첫 컷(= SSR 히어로)이 S1 이 아니면 숨김 — 「낮」 을 누를 곳이 없다.
 *   · S1·S2·S4 중 가진 것만. 그 수가 2 미만이면 숨김(고를 것이 없다).
 */
export function heroTimeOptionsOf(shots: readonly DetailShot[] | null | undefined): HeroTimeOption[] {
  const list = shots ?? [];
  if (list.length < HERO_TIME_MIN_SHOTS) return [];
  if (list[0]?.shot !== HERO_SHOT_DAY) return [];
  const out: HeroTimeOption[] = [];
  for (const t of HERO_TIME_ORDER) {
    const s = list.find((x) => x.shot === t.shot);
    if (s) out.push({ ...s, label: t.label });
  }
  return out.length >= 2 ? out : [];
}

/** 방문자 «로컬» 시(0~23) → 보여 줄 컷. 18·19시 노을 · 20시~05시 밤 · 그 밖 낮. 그 컷이 없으면 낮(S1). */
export function heroShotForHour(hour: number, available: readonly string[] | null | undefined): string {
  const h = Number.isFinite(hour) ? ((Math.floor(hour) % 24) + 24) % 24 : 12;
  const want = h >= 18 && h < 20 ? HERO_SHOT_GOLDEN : (h >= 20 || h < 6) ? HERO_SHOT_NIGHT : HERO_SHOT_DAY;
  return (available ?? []).includes(want) ? want : HERO_SHOT_DAY;
}

/** 라디오 묶음 키보드 — ←↑ 앞 · →↓ 뒤(끝에서 돈다) · Home/End. 그 밖의 키는 null. */
export function radioKeyIndex(key: string, index: number, count: number): number | null {
  if (count <= 0) return null;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return (index - 1 + count) % count;
  if (key === 'ArrowRight' || key === 'ArrowDown') return (index + 1) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

/** hero_time_toggle 계측 상한 — 세션당 3회. */
export const HERO_TIME_TRACK_CAP = 3;
export function canTrackHeroTime(sentSoFar: number, cap: number = HERO_TIME_TRACK_CAP): boolean {
  return Number.isFinite(sentSoFar) && sentSoFar >= 0 && sentSoFar < cap;
}

/* ───────────────────────────── D5 전체 화면 갤러리 ───────────────────────────── */

export type LightboxSlide = { kind: 'shot'; shot: DetailShot } | { kind: 'lead' };

/** 하단 캡션 첫 줄 — 어느 컷에서나 «항상» 선다. */
export const LIGHTBOX_NOTICE = 'AI 상상도 · 실제와 다를 수 있음';

/** 장면 순서 — 컷 전부(S1→S4) 다음, 리드폼이 있는 현장에서만 마지막 «뒤» 에 관심고객 카드 1장. */
export function lightboxSlidesOf(shots: readonly DetailShot[] | null | undefined, hasLead: boolean): LightboxSlide[] {
  const out: LightboxSlide[] = (shots ?? []).map((shot) => ({ kind: 'shot' as const, shot }));
  if (out.length > 0 && hasLead) out.push({ kind: 'lead' });
  return out;
}

/** 열 때의 첫 장면 — 그 컷이 없으면 0. */
export function lightboxStartIndex(slides: readonly LightboxSlide[], shot: string | null | undefined): number {
  const i = slides.findIndex((s) => s.kind === 'shot' && s.shot.shot === shot);
  return i >= 0 ? i : 0;
}

/** 넘기기 — 끝에서 돌지 않는다(관심고객 카드가 «끝» 이어야 한다). */
export function stepSlide(index: number, dir: number, count: number): number {
  if (count <= 0) return 0;
  const next = index + (dir > 0 ? 1 : dir < 0 ? -1 : 0);
  return Math.min(count - 1, Math.max(0, next));
}

export const SWIPE_MIN_PX = 48;
/** 가로 쓸기 판정 — 왼쪽으로 쓸면 다음(+1), 오른쪽이면 이전(-1). 세로가 더 크거나 짧으면 0. */
export function swipeDirOf(dx: number, dy: number, min: number = SWIPE_MIN_PX): -1 | 0 | 1 {
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return 0;
  if (Math.abs(dx) < min || Math.abs(dx) <= Math.abs(dy) * 1.2) return 0;
  return dx < 0 ? 1 : -1;
}

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;
export const ZOOM_DOUBLE_TAP = 2;
export function clampZoom(scale: number): number {
  if (!Number.isFinite(scale)) return ZOOM_MIN;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
}

/**
 * 확대된 그림의 이동 한계 — 그림(imgW×imgH · 배율 scale)이 무대(stageW×stageH)보다 넘친 만큼의 절반까지만 움직인다
 * (빈 곳을 드러내지 않는다). 넘치지 않는 축은 0 — 배율 1 로 무대 안에 든 그림은 (0,0).
 */
export function clampPan(x: number, y: number, scale: number, imgW: number, imgH: number, stageW: number, stageH: number): { x: number; y: number } {
  const s = clampZoom(scale);
  const n = (v: number) => (Number.isFinite(v) && v > 0 ? v : 0);
  const maxX = Math.max(0, (n(imgW) * s - n(stageW)) / 2);
  const maxY = Math.max(0, (n(imgH) * s - n(stageH)) / 2);
  const cx = Number.isFinite(x) ? x : 0;
  const cy = Number.isFinite(y) ? y : 0;
  // `|| 0` — 한계가 0 인 축에서 -0 이 나오지 않게 한다.
  return { x: Math.min(maxX, Math.max(-maxX, cx)) || 0, y: Math.min(maxY, Math.max(-maxY, cy)) || 0 };
}

/* ───────────────────────────── R17 신고 ───────────────────────────── */

export const IMAGE_REPORT_ENDPOINT = '/api/site-image-report';
export const IMAGE_REPORT_STORE_KEY = 'kd_img_report_v1';
export const IMAGE_REPORT_STORE_MAX = 200;

/** 같은 컷 중복 방지 키 — 현장 id(소문자) + 컷. 둘 중 하나라도 비면 null(신고 불가). */
export function reportKeyOf(siteId: string | null | undefined, shot: string | null | undefined): string | null {
  const id = (siteId ?? '').trim().toLowerCase();
  const s = (shot ?? '').trim();
  return id && s ? `${id}:${s}` : null;
}

/** 저장 문자열(JSON 배열) → 키 목록. 깨진 값은 빈 목록. */
export function parseReported(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length < 120) : [];
  } catch { return []; }
}

export function hasReported(list: readonly string[], key: string | null): boolean {
  return !!key && list.includes(key);
}

/** 키 추가(이미 있으면 그대로) — 오래된 것부터 버려 상한을 지킨다. */
export function addReported(list: readonly string[], key: string | null, max: number = IMAGE_REPORT_STORE_MAX): string[] {
  if (!key) return [...list];
  if (list.includes(key)) return [...list];
  return [...list, key].slice(-max);
}

/* ───────────────────────────── R12 구조화 데이터 ───────────────────────────── */

type ResolvedLike = { kind?: string | null; shots?: readonly { master?: { url?: string | null } | null }[] | null } | null | undefined;

/** 생성 컷 «전부» 의 master JPEG 절대 URL(S1→S4). 생성 판정이 아니면 빈 배열. */
export function masterJpegUrlsOf(resolved: ResolvedLike): string[] {
  if (!resolved || resolved.kind !== 'generated') return [];
  const out: string[] = [];
  for (const s of resolved.shots ?? []) {
    const u = typeof s?.master?.url === 'string' ? s.master.url.trim() : '';
    // 리졸버의 색인 경로(`{origin}/img/…jpg`)만 — Storage 직링크(`/storage/v1/…`)·webp 는 걸러진다. 비프로덕션 origin 오버라이드(http://localhost)도 통과.
    if (/^https?:\/\/[^/]+\/img\/.+\.jpe?g$/i.test(u) && !out.includes(u)) out.push(u);
  }
  return out;
}

/**
 * Residence(등 Place 류) JSON-LD 의 image 값.
 *   · 상상도가 없으면 «종전 값 그대로»(문자열) — 공식 사진 현장·미보유 현장의 출력 불변.
 *   · 있으면 master JPEG 전부 + 종전 값(맨 뒤).
 */
export function residenceImageOf(masterUrls: readonly string[] | null | undefined, fallback: string): string | string[] {
  const urls = (masterUrls ?? []).filter((u) => typeof u === 'string' && u.trim() !== '');
  return urls.length > 0 ? [...urls, fallback] : fallback;
}

/* ───────────────────────────── R5 슬라이더 폴백 ───────────────────────────── */

/**
 * 「지금 → 완공」 2단 이미지 주소 검사 — 자체 도메인 `/img/…jpg`(절대 kadeora.app 또는 같은 출처 상대경로)만 통과.
 * Storage 직링크·다른 호스트·다른 확장자는 null(= 종전 세로 쌓기 폴백).
 */
export function safeDuoSrc(src: string | null | undefined): string | null {
  const u = (src ?? '').trim();
  if (!u) return null;
  return /^(?:https:\/\/kadeora\.app)?\/img\/[A-Za-z0-9_\-./]+\.jpe?g$/.test(u) && !u.includes('..') ? u : null;
}

/* ───────────────────────────── R11 공유 카드 (레인 E) ───────────────────────────── */

/**
 * 카카오 공유 `imageUrl` 로 넘길 주소 검사 — «절대» `https://kadeora.app/img/{site_id}/ch/card-{shot}-1x1-{hash}.jpg` 만 통과.
 *   카카오 서버가 내려받는 주소라 상대경로·로컬 origin·Storage 직링크·다른 채널은 undefined(= ShareDock 의 기존 체인 /api/og).
 */
export function shareCardUrlOf(src: string | null | undefined): string | undefined {
  const u = (src ?? '').trim();
  return /^https:\/\/kadeora\.app\/img\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/ch\/card-S[0-9]_[a-z_]+-1x1-[A-Za-z0-9]+\.jpg$/.test(u) ? u : undefined;
}
